import type { AnalyticsReport } from "@/app/analyticsQueries";
import { BAR_BG, TONE_TEXT, barTone, daysTone, fmtDays, fmtIter, iterTone, reachedLabel } from "./model";

/** Воронка по шагам: дошло / сдало гейт / медиана дней / итераций. Правила строк — §3 п.6. */
export function Funnel({ report, note }: { report: AnalyticsReport; note: string }) {
  return (
    <section className="overflow-hidden rounded-md border border-bd bg-card" data-testid="funnel">
      <div className="flex flex-wrap items-baseline gap-3 border-b border-bd px-4 py-3">
        <h2 className="m-0 font-mono text-[14px] font-bold">Воронка по шагам</h2>
        <span className="font-mono text-[11.5px] text-mut">сколько студентов дошло до шага и сдало его гейт</span>
        <span className="flex-1" />
        <span className="font-mono text-[11.5px] text-ink2" data-testid="funnel-note">
          {note}
        </span>
      </div>
      {report.funnel.length === 0 ? (
        <p className="m-0 px-4 py-6 text-[13px] text-ink2">Маршрут пуст — воронка появится, когда в курсе будут шаги с гейтами.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[.04em] text-mut">
                <th className="px-4 py-2 text-left font-medium">шаг</th>
                <th className="px-3 py-2 text-right font-medium">дошло</th>
                <th className="px-3 py-2 text-left font-medium">сдало гейт</th>
                <th className="px-3 py-2 text-right font-medium">медиана</th>
                <th className="px-4 py-2 text-right font-medium">итераций</th>
              </tr>
            </thead>
            <tbody>
              {report.funnel.map((row) => {
                const tone = barTone(row.pct);
                return (
                  <tr key={row.step.id} className={`border-t border-bd ${row.bad ? "bg-bg2" : ""}`} data-testid="funnel-row" data-bad={row.bad || undefined} data-tone={tone}>
                    <td className="px-4 py-2.5">
                      <div className={`text-[13px] ${row.bad ? "font-medium text-ink" : "text-ink2"}`}>{row.step.title}</div>
                      <div className="font-mono text-[10.5px] text-mut">
                        {row.step.track} · шаг {row.step.index}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-[12px] text-ink2" data-testid="funnel-reached">
                      {reachedLabel(row, report.top)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="h-[6px] w-[140px] flex-none overflow-hidden rounded-[3px] bg-card2" role="img" aria-label={`${row.passed} из ${row.reached}, ${row.pct}%`}>
                          <span className={`block h-full ${BAR_BG[tone]}`} style={{ width: `${Math.min(100, row.pct)}%` }} data-testid="funnel-bar" />
                        </span>
                        <span className={`font-mono text-[12px] ${tone === "none" ? "text-mut" : TONE_TEXT[tone]}`} data-testid="funnel-pct">
                          {row.pct}%
                        </span>
                        <span className="font-mono text-[10.5px] text-mut">{row.passed}</span>
                      </div>
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono text-[12px] ${TONE_TEXT[daysTone(row.median_days)]}`} data-testid="funnel-days">
                      {fmtDays(row.median_days)}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-mono text-[12px] ${TONE_TEXT[iterTone(row.iterations)]}`} data-testid="funnel-iter">
                      {fmtIter(row.iterations)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
